import { Container, Graphics, Sprite, Texture, Assets } from 'pixi.js';
import type { FluffleProject } from '@/data/projects';
import {
  getProjectByAddress,
  getProjectByName,
  getCircleKeyForAddress,
  isProjectKey,
  getProjectNameFromKey,
  getAddressesForCircleKey,
  getPrimaryAddressForCircleKey
} from '@/data/projects';
import { userSettings } from '@/stores/userSettings';
import type { WebSocketClient } from '@/core/WebSocketClient';
import {
  SCENE_WIDTH,
  SCENE_HEIGHT,
  INITIAL_CIRCLE_COUNT,
  CIRCLE_COUNT,
  CIRCLE_SPAWN_RATE,
  MIN_CIRCLE_RADIUS,
  MAX_CIRCLE_RADIUS,
  CIRCLE_COLOR,
  CIRCLE_ALPHA,
  SEQUENCER_CENTER_X,
  SEQUENCER_CENTER_Y,
  SEQUENCER_HEIGHT,
  SEQUENCER_EXCLUSION_RADIUS,
  BLOCKS_BOTTOM,
  BLOCK_COLOR,
  SAVED_CIRCLE_COLOR,
  MAX_VISIBLE_CIRCLES,
} from '../config/visualization.config';

/**
 * CircleRenderer - dApp Activity Circle Visualization
 *
 * Manages the interactive circle visualization where each circle represents a dApp or smart contract.
 * Circles grow/shrink based on transaction activity and transactions animate from circles to the sequencer.
 *
 * ## Architecture Overview
 *
 * The renderer uses a spatial grid for efficient collision detection and overlap resolution.
 * Circles are uniquely identified by a "circleKey" which is either:
 * - "project:{projectName}" for known dApps (aggregates multiple contracts)
 * - Raw address (0x...) for unknown contracts
 *
 * ## Key Sections
 *
 * 1. **Configuration** (lines ~100-200): Constants for physics, sizing, timing
 * 2. **Circle Lookup** (lines ~248-315): Methods to find circles by address
 * 3. **Save/Unsave** (lines ~317-465): Persistence of user-pinned circles
 * 4. **State Management** (lines ~465-630): Initial state, testing mode, bounds
 * 5. **Position Finding** (lines ~698-1050): Spiral algorithm, grid management
 * 6. **Animation Loop** (lines ~1092-1500): Main update cycle, ranking, removal
 * 7. **Drag & Drop** (lines ~1642-1900): User interaction, physics-based repulsion
 * 8. **Collision Detection** (lines ~1557-1600): Overlap checking
 *
 * ## Key Concepts
 *
 * - **Target Radius**: Based on ranking by gas usage; top circles are larger
 * - **Saved Circles**: User-pinned circles that persist across sessions
 * - **Deleted Circles**: Soft-deleted circles that can be restored
 * - **Circle Key**: Unique identifier that groups related contract addresses
 *
 * ## Performance Notes
 *
 * - Spatial grid (GRID_CELL_SIZE) provides O(1) nearby circle lookup
 * - Circle count capped at MAX_VISIBLE_CIRCLES (20)
 * - Overlap resolution uses repulsion physics to avoid expensive repositioning
 */

interface CircleData {
  circleKey: string; // Circle key: "project:{name}" for known dapps, or raw address for unknown
  address: string; // Primary address (for backwards compatibility)
  addresses: string[]; // All addresses associated with this circle (for multi-contract dapps)
  x: number;
  y: number;
  radius: number;
  baseRadius: number; // Minimum radius (5px)
  maxRadius: number; // Maximum radius (50px)
  targetRadius: number; // Target radius based on ranking
  txCount: number; // Number of transactions to this address (aggregated for multi-contract)
  gasUsed: number; // Total gas used by transactions to this address (aggregated)
  lastActivityTime: number; // Timestamp of last transaction
  graphics: Graphics | null; // Graphics object for rendering
  sprite: Sprite | null; // Sprite for project image (loaded when largest)
  hasImage: boolean; // Whether an image has been loaded for this circle
  imageUrl: string | null; // URL of the loaded image
  projectData: FluffleProject | null; // Project data for the loaded image

  // Stats (initialized from backend on page load, incremented on new tx)
  lastMinuteCount: number; // Transaction count in last 60 seconds (aggregated)
  tps: number; // Transactions per second (lastMinuteCount / 60) (aggregated)

  // Drag physics state
  velocityX: number; // Velocity for physics simulation
  velocityY: number;

  // Saved state (user manually placed this circle)
  isSaved: boolean;
  restoredFromStorage: boolean; // True if this circle was restored from localStorage on page load

  // Highlight state (when modal is open)
  highlightGraphics: Graphics | null; // Graphics object for highlight ring
}

interface PendingAddressData {
  txCount: number;
  gasUsed: number;
  firstSeenTime: number;
  lastActivityTime: number;
}

export interface CircleMetrics {
  tps: number; // Transactions per second (over last 60 seconds)
  gps: number; // Gas per second (gasUsed / 60)
  lastMinuteCount: number; // Transactions in last 60 seconds
  totalTxCount: number; // Total lifetime transactions
  totalGasUsed: number; // Total lifetime gas used
}

export class CircleRenderer {
  private container: Container;
  private circleKeyToCircle: Map<string, CircleData> = new Map(); // Map circle keys to circles
  private addressToCircleKey: Map<string, string> = new Map(); // Reverse lookup: address -> circle key
  private pendingAddresses: Map<string, PendingAddressData> = new Map(); // Addresses below threshold (no circle yet)
  private chartMode: 'gas' | 'txCount' = 'txCount'; // Current chart mode
  private onCircleClick: ((project: FluffleProject | null, address: string, mouseX: number, mouseY: number, metrics: CircleMetrics) => void) | null = null; // Callback for circle clicks

  // Highlighted circles (when modals are open)
  private highlightedCircleKeys: Set<string> = new Set();
  private readonly HIGHLIGHT_COLOR = 0x00FFFF; // Cyan highlight ring
  private highlightPhase = 0; // Animation phase for pulsing effect

  // Spatial hashing for O(1) collision detection
  private spatialGrid: Map<string, CircleData[]> = new Map();
  private readonly GRID_CELL_SIZE = 100; // Grid cell size in pixels

  // Circle sizing constants - use config values
  private readonly BASE_RADIUS = MIN_CIRCLE_RADIUS; // Minimum radius (10px from config)
  private readonly MAX_RADIUS = MAX_CIRCLE_RADIUS; // Maximum radius (50px from config)
  private readonly MAX_CIRCLES = MAX_VISIBLE_CIRCLES; // Maximum number of non-saved circles (20 from config)
  private readonly RANKING_UPDATE_INTERVAL = 100; // Recalculate rankings every 100ms (was 2000ms)
  private readonly MIN_CIRCLE_GAP = 5; // Minimum pixels between circle edges (increased for larger circles)
  private readonly CIRCLE_INACTIVITY_TIMEOUT = 60000; // Remove circles/pending after 60 seconds of inactivity

  // Dynamic threshold state (unlimited, scales with demand)
  private dynamicThreshold = 1; // Current threshold (starts at 1, grows infinitely based on competition)
  private lastThresholdAdjustment = 0; // Timestamp of last threshold calculation

  // Off-screen spawn radius (transactions spawn from just outside visible area)
  private readonly OFF_SCREEN_RADIUS = 5;

  // Ranking update tracking
  private lastRankingUpdate = 0; // Timestamp of last ranking calculation
  private lastRadiusClampUpdate = 0; // Timestamp of last radius clamping
  private readonly RADIUS_CLAMP_INTERVAL = 200; // Only clamp radii every 200ms (OPTIMIZATION)

  // Initial spawn delay state (smart positioning on page load)
  private initialSpawnComplete: boolean = false;
  private initialSpawnQueue: Map<string, { gas: number; tx: number }> = new Map();
  private initialSpawnTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly INITIAL_SPAWN_DELAY_MS = 500; // Wait for activity data before spawning

  // Testing mode state (when txMultiplier > 1)
  private isTestingMode = false;
  private testingModeAddresses: Set<string> = new Set(); // Track addresses created for testing

  // Drag mode state
  private isDragMode = false; // When true, physics simulation runs

  // WebSocket client for address subscriptions (saved circles)
  private wsClient: WebSocketClient | null = null;
  private draggedCircle: CircleData | null = null; // Currently dragged circle
  private dragOffsetX = 0; // Mouse offset from circle center
  private dragOffsetY = 0;
  private viewportScale = 1; // Scale factor for converting screen to world coords
  private canvasRect: DOMRect | null = null; // Canvas bounding rect for coordinate conversion
  private readonly DRAG_REPULSION_FORCE = 0.5; // How strongly circles push each other
  private readonly DRAG_FRICTION = 0.85; // Velocity damping (lower = more friction)
  private readonly DRAG_MIN_VELOCITY = 0.1; // Stop physics when all velocities below this

  // Spawn area constraints (dynamic - recalculated on resize)
  private MIN_Y: number;
  private MAX_Y: number;
  private SCENE_WIDTH: number;
  private SCENE_HEIGHT: number;
  private readonly SEQUENCER_CENTER_X: number;
  private readonly SEQUENCER_CENTER_Y: number;
  private readonly SEQUENCER_BOTTOM_Y: number;
  private readonly MIN_DISTANCE_FROM_SEQUENCER = 50;
  private readonly MIN_DISTANCE_FROM_SEQUENCER_SQ: number;

  constructor(container: Container, worldWidth: number, worldHeight: number) {
    this.container = container;

    // Calculate sequencer position (from config constants)
    this.SEQUENCER_CENTER_X = SEQUENCER_CENTER_X;
    this.SEQUENCER_CENTER_Y = SEQUENCER_CENTER_Y;

    // Calculate sequencer bottom: center + half height
    // SEQUENCER_HEIGHT = 120, so bottom = SEQUENCER_CENTER_Y + 60
    this.SEQUENCER_BOTTOM_Y = SEQUENCER_CENTER_Y + (SEQUENCER_HEIGHT / 2); // 280 + 60 = 340

    // Initialize scene dimensions with actual viewport size (not static constants)
    this.SCENE_WIDTH = worldWidth;
    this.SCENE_HEIGHT = worldHeight;

    // Circles must be positioned below sequencer bottom + 10px padding
    this.MIN_Y = this.SEQUENCER_BOTTOM_Y + 10 + this.MAX_RADIUS; // Add MAX_RADIUS so circle tops don't go above limit
    this.MAX_Y = this.SCENE_HEIGHT; // Spawn to bottom edge (circles can extend past bottom)

    this.MIN_DISTANCE_FROM_SEQUENCER_SQ = this.MIN_DISTANCE_FROM_SEQUENCER * this.MIN_DISTANCE_FROM_SEQUENCER;

    // console.log(`🔵 CircleRenderer initialized: world ${worldWidth}x${worldHeight}, circle area Y=${this.MIN_Y}-${this.MAX_Y}`);
  }

  /**
   * Set chart mode (gas or txCount) to determine circle growth metric
   * Forces immediate recalculation of rankings and circle sizes
   */
  public setChartMode(mode: 'gas' | 'txCount'): void {
    if (this.chartMode === mode) return; // No change
    this.chartMode = mode;
    this.lastRankingUpdate = 0; // Force immediate ranking recalculation on next update
    // console.log(`🔵 Chart mode changed to ${mode} - forcing immediate recalculation`);
  }

  /**
   * Set callback for circle clicks
   */
  public setCircleClickCallback(callback: (project: FluffleProject | null, address: string, mouseX: number, mouseY: number, metrics: CircleMetrics) => void): void {
    this.onCircleClick = callback;
    // console.warn('🔵 Circle click callback registered');
  }

  /**
   * Set viewport scale and canvas for coordinate conversion during drag
   */
  public setViewportInfo(scale: number, canvas: HTMLCanvasElement): void {
    this.viewportScale = scale;
    this.canvasRect = canvas.getBoundingClientRect();
  }

  /**
   * Set WebSocket client for address subscriptions
   * Used to subscribe to saved circles for real-time updates
   */
  public setWebSocketClient(client: WebSocketClient): void {
    this.wsClient = client;

    // Batch subscribe to all existing saved circles
    const savedAddresses: string[] = [];
    for (const circle of this.circleKeyToCircle.values()) {
      if (circle.isSaved) {
        savedAddresses.push(circle.address);
      }
    }
    if (savedAddresses.length > 0) {
      this.wsClient.subscribeBatch(savedAddresses);
    }
  }

  /**
   * Set which circles should be highlighted (used when modals are open)
   * Pass addresses and they will be converted to circle keys internally
   */
  public setHighlightedCircles(addresses: string[]): void {
    const newKeys = new Set<string>();
    for (const address of addresses) {
      const normalizedAddress = address.toLowerCase();
      // First try reverse map lookup
      let circleKey = this.addressToCircleKey.get(normalizedAddress);
      if (!circleKey) {
        // Fallback to computing the key
        circleKey = getCircleKeyForAddress(normalizedAddress);
      }
      // Only add if we have a circle for this key
      if (this.circleKeyToCircle.has(circleKey)) {
        newKeys.add(circleKey);
      }
    }

    // Remove highlight graphics from circles no longer highlighted
    for (const oldKey of this.highlightedCircleKeys) {
      if (!newKeys.has(oldKey)) {
        const circle = this.circleKeyToCircle.get(oldKey);
        if (circle?.highlightGraphics) {
          this.container.removeChild(circle.highlightGraphics);
          circle.highlightGraphics.destroy();
          circle.highlightGraphics = null;
        }
      }
    }

    this.highlightedCircleKeys = newKeys;
  }

  /**
   * Get the circle key for an address (for external use)
   */
  public getCircleKeyForAddressExternal(address: string): string | null {
    const normalizedAddress = address.toLowerCase();
    const circleKey = this.addressToCircleKey.get(normalizedAddress);
    if (circleKey && this.circleKeyToCircle.has(circleKey)) {
      return circleKey;
    }
    const computedKey = getCircleKeyForAddress(normalizedAddress);
    if (this.circleKeyToCircle.has(computedKey)) {
      return computedKey;
    }
    return null;
  }

  /**
   * Get circle by address (looks up through circle key)
   * Returns null if address doesn't have a circle
   */
  private getCircleByAddress(address: string): CircleData | null {
    const normalizedAddress = address.toLowerCase();
    // First try direct lookup in reverse map
    const circleKey = this.addressToCircleKey.get(normalizedAddress);
    if (circleKey) {
      return this.circleKeyToCircle.get(circleKey) || null;
    }
    // Fallback: compute the circle key and try lookup
    const computedKey = getCircleKeyForAddress(normalizedAddress);
    return this.circleKeyToCircle.get(computedKey) || null;
  }

  /**
   * Check if an address has a circle
   */
  public hasCircleForAddress(address: string): boolean {
    return this.getCircleByAddress(address) !== null;
  }

  /**
   * Migrate circles to new keys after OLI labels are updated
   * This prevents duplicate circles when labels change address->project mapping
   */
  public migrateCircleKeys(): void {
    const migrations: Array<{ oldKey: string; newKey: string; circle: CircleData }> = [];

    // Find all circles that need migration
    for (const [oldKey, circle] of this.circleKeyToCircle) {
      // Skip if already a project key - check if it should merge with another
      const addresses = circle.addresses;
      if (addresses.length === 0) continue;

      // Get the NEW key for this circle's primary address
      const primaryAddr = addresses[0];
      const newKey = getCircleKeyForAddress(primaryAddr);

      // If key changed, need to migrate
      if (newKey !== oldKey) {
        migrations.push({ oldKey, newKey, circle });
      }
    }

    // Perform migrations
    for (const { oldKey, newKey, circle } of migrations) {
      // Check if target key already exists (need to merge)
      const existingCircle = this.circleKeyToCircle.get(newKey);

      if (existingCircle && existingCircle !== circle) {
        // Merge: transfer stats to existing circle, remove old one
        existingCircle.txCount += circle.txCount;
        existingCircle.gasUsed += circle.gasUsed;
        existingCircle.lastMinuteCount += circle.lastMinuteCount;
        existingCircle.tps += circle.tps;

        // Add addresses from old circle to existing
        for (const addr of circle.addresses) {
          if (!existingCircle.addresses.includes(addr)) {
            existingCircle.addresses.push(addr);
          }
          this.addressToCircleKey.set(addr, newKey);
        }

        // Remove old circle
        this.removeFromGrid(circle);
        if (circle.graphics) {
          this.container.removeChild(circle.graphics);
          circle.graphics.destroy();
        }
        this.circleKeyToCircle.delete(oldKey);
      } else {
        // Simple migration: just update the key
        this.circleKeyToCircle.delete(oldKey);
        this.circleKeyToCircle.set(newKey, circle);

        // Update reverse lookup for all addresses
        for (const addr of circle.addresses) {
          this.addressToCircleKey.set(addr, newKey);
        }

        // Try to load project image if we now have one
        if (isProjectKey(newKey) && !circle.sprite) {
          const projectName = getProjectNameFromKey(newKey);
          if (projectName) {
            const project = getProjectByName(projectName);
            if (project?.img) {
              this.loadImageForCircle(circle, newKey, project);
            }
          }
        }
      }
    }
  }

  /**
   * Get current metrics for a specific address
   * Returns null if address doesn't have a circle
   */
  public getMetricsForAddress(address: string): CircleMetrics | null {
    const circle = this.getCircleByAddress(address.toLowerCase());
    if (!circle) return null;

    return {
      tps: circle.tps,
      gps: circle.gasUsed / 60, // Gas per second
      lastMinuteCount: circle.lastMinuteCount,
      totalTxCount: circle.txCount,
      totalGasUsed: circle.gasUsed
    };
  }

  /**
   * Get project name for an address
   * Looks up project name from the address mapping (doesn't require a circle or loaded image)
   */
  public getProjectNameForAddress(address: string): string | null {
    // First try to get project directly from address mapping (works even if no circle exists)
    const project = getProjectByAddress(address.toLowerCase());
    if (project?.name) return project.name;

    // Fallback: check circle's projectData (in case it was loaded from image)
    const circle = this.getCircleByAddress(address.toLowerCase());
    if (circle?.projectData?.name) return circle.projectData.name;

    return null;
  }

  /**
   * Get project data for an address
   * Returns the Project object or null if not found
   */
  public getProjectForAddress(address: string): FluffleProject | null {
    // Try to get from address mapping
    const project = getProjectByAddress(address.toLowerCase());
    if (project) return project;

    // Fallback: check circle's projectData
    const circle = this.getCircleByAddress(address.toLowerCase());
    return circle?.projectData || null;
  }

  /**
   * Check if a circle is saved (user manually placed it)
   */
  public isCircleSaved(address: string): boolean {
    const circle = this.getCircleByAddress(address.toLowerCase());
    return circle?.isSaved ?? false;
  }

  /**
   * Save a circle's current position to localStorage
   */
  public saveCirclePosition(address: string): void {
    const circle = this.getCircleByAddress(address.toLowerCase());
    if (!circle) return;

    // Save to localStorage using the primary address
    userSettings.saveCirclePosition(circle.address, circle.x, circle.y);

    // Update circle state
    circle.isSaved = true;

    // Subscribe to this address for real-time updates from backend
    if (this.wsClient) {
      this.wsClient.subscribe(circle.address);
    }

    // Redraw with saved color (orange)
    if (circle.graphics) {
      circle.graphics.clear();
      circle.graphics.circle(0, 0, circle.radius);
      circle.graphics.fill({ color: SAVED_CIRCLE_COLOR, alpha: 0.01 });
      circle.graphics.stroke({ width: 1, color: SAVED_CIRCLE_COLOR });
      const r = circle.radius;
      circle.graphics.hitArea = { contains: (x: number, y: number) => x * x + y * y <= r * r };
    }

    // console.log(`🔵 Circle position saved for ${circle.circleKey}`);
  }

  /**
   * Check if there are any SAVED circles that need syncing
   * Returns true if there are saved circles that:
   * 1. Were restored from localStorage on page load (not saved during this session)
   * 2. Have NO data yet (txCount is 0 - weren't in initial_state from backend)
   */
  public hasCirclesNeedingSync(): boolean {
    for (const circle of this.circleKeyToCircle.values()) {
      // Only check circles restored from storage that have no data yet
      // Circles saved during this session already have live data - no sync needed
      if (circle.restoredFromStorage && circle.lastMinuteCount === 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Remove a circle from saved state (reset to default behavior)
   * Changes color back to blue and removes from localStorage
   */
  public unsaveCircle(address: string): void {
    const normalizedAddress = address.toLowerCase();
    const circle = this.getCircleByAddress(normalizedAddress);

    if (!circle) return;

    // Unsubscribe from this address (no longer need real-time updates)
    if (this.wsClient) {
      this.wsClient.unsubscribe(circle.address);
    }

    // Remove from localStorage - remove all addresses associated with this circle
    for (const addr of circle.addresses) {
      userSettings.removeSavedCircle(addr);
    }

    // Update circle state
    circle.isSaved = false;

    // Redraw circle with default color (blue)
    if (circle.graphics) {
      circle.graphics.clear();
      circle.graphics.circle(0, 0, circle.radius);
      circle.graphics.fill({ color: BLOCK_COLOR, alpha: 0.01 });
      circle.graphics.stroke({ width: 1, color: BLOCK_COLOR });
      // OPTIMIZATION: Use contains function instead of new Graphics()
      const r = circle.radius;
      circle.graphics.hitArea = { contains: (x: number, y: number) => x * x + y * y <= r * r };
    }

    // console.log(`🔵 Circle unsaved for ${circle.circleKey}`);
  }

  /**
   * Check if a circle is deleted (hidden by user)
   */
  public isCircleDeleted(address: string): boolean {
    return userSettings.isCircleDeleted(address);
  }

  /**
   * Delete a circle (hide it from view)
   * Transactions will spawn from off-screen instead
   */
  public deleteCircle(address: string): void {
    const normalizedAddress = address.toLowerCase();
    const circle = this.getCircleByAddress(normalizedAddress);

    // Get project name for display in settings
    const projectName = circle?.projectData?.name;

    // Unsubscribe from this address (no longer tracking)
    if (this.wsClient && circle) {
      this.wsClient.unsubscribe(circle.address);
    }

    // Add all addresses for this circle to deleted list
    if (circle) {
      for (const addr of circle.addresses) {
        userSettings.deleteCircle(addr, projectName);
      }
      this.removeCircleByKey(circle.circleKey);
    } else {
      // No circle exists, just mark the address as deleted
      userSettings.deleteCircle(normalizedAddress, projectName);
    }

    // console.log(`🔵 Circle deleted for ${projectName || normalizedAddress}`);
  }

  /**
   * Restore a deleted circle (allow it to appear again)
   */
  public restoreDeletedCircle(address: string): void {
    const normalizedAddress = address.toLowerCase();
    userSettings.restoreCircle(normalizedAddress);
    // console.log(`🔵 Circle restored for ${normalizedAddress}`);
  }

  /**
   * Get project name for a deleted circle (from userSettings)
   */
  public getDeletedCircleProjectName(address: string): string | undefined {
    const deletedCircles = userSettings.deletedCircles;
    return deletedCircles[address.toLowerCase()]?.projectName;
  }

  /**
   * Set initial state from backend (historical data from last 60 seconds)
   * Stores data as pending - circles will be created after a short delay with smart positioning
   */
  public setInitialState(projects: Array<{ address: string; gasUsed: number; txCount: number }>): void {
    // console.log(`📦 CircleRenderer: Received ${projects.length} projects from initial state`);

    const now = performance.now();

    // Store all projects as pending addresses AND queue for smart initial spawn
    for (const project of projects) {
      const address = project.address.toLowerCase();

      // Skip if already exists
      if (this.hasCircleForAddress(address) || this.pendingAddresses.has(address)) {
        continue;
      }

      // Add to pending with historical activity
      this.pendingAddresses.set(address, {
        txCount: project.txCount,
        gasUsed: project.gasUsed,
        firstSeenTime: now,
        lastActivityTime: now
      });

      // Queue for smart initial spawn (unless it has a saved position)
      const circleKey = getCircleKeyForAddress(address);
      if (!this.hasSavedPosition(circleKey)) {
        const existing = this.initialSpawnQueue.get(circleKey) || { gas: 0, tx: 0 };
        this.initialSpawnQueue.set(circleKey, {
          gas: existing.gas + project.gasUsed,
          tx: existing.tx + project.txCount
        });
      }
    }

    // console.log(`📦 Stored ${this.pendingAddresses.size} pending, ${this.initialSpawnQueue.size} queued for smart spawn`);

    // IMPORTANT: Create circles for saved addresses right away (they have saved positions)
    const savedCircles = userSettings.savedCircles;
    for (const address of Object.keys(savedCircles)) {
      // Skip if already exists or is deleted
      if (this.hasCircleForAddress(address)) continue;
      if (userSettings.isCircleDeleted(address)) continue;

      // Create the saved circle - stats will be set by updateFromStatsUpdate()
      const circleKey = getCircleKeyForAddress(address);
      const newCircle = this.createCircleForKey(circleKey);
      if (newCircle) {
        // Check if we have pending data for any of the addresses (from initial_state)
        for (const addr of newCircle.addresses) {
          const pending = this.pendingAddresses.get(addr);
          if (pending) {
            // Aggregate stats from all addresses
            newCircle.txCount += pending.txCount;
            newCircle.gasUsed += pending.gasUsed;
            newCircle.lastMinuteCount += pending.txCount;
            this.pendingAddresses.delete(addr);
          }
        }
        newCircle.tps = newCircle.lastMinuteCount / 60;
        // Mark as restored from storage (for sync indicator logic)
        newCircle.restoredFromStorage = true;
        // console.log(`🟠 Restored saved circle for ${circleKey} from initial state`);
      }
    }

    // Batch subscribe to all saved circles (fixes race condition - this is called after circles are loaded)
    if (this.wsClient) {
      const savedAddresses = Object.keys(savedCircles).filter(addr => !userSettings.isCircleDeleted(addr));
      if (savedAddresses.length > 0) {
        this.wsClient.subscribeBatch(savedAddresses);
      }
    }

    // Start timer for smart initial spawn (gives time for miniblock_updates to arrive with more data)
    if (this.initialSpawnTimer) {
      clearTimeout(this.initialSpawnTimer);
    }
    this.initialSpawnTimer = setTimeout(() => {
      this.performInitialSpawn();
    }, this.INITIAL_SPAWN_DELAY_MS);
  }

  /**
   * Check if a circle key has a saved position
   */
  private hasSavedPosition(circleKey: string): boolean {
    const addresses = getAddressesForCircleKey(circleKey);
    for (const addr of addresses) {
      if (userSettings.getSavedCirclePosition(addr)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Perform smart initial spawn - position circles based on activity with good distribution
   * Called after a short delay to allow initial activity data to accumulate
   */
  private performInitialSpawn(): void {
    this.initialSpawnComplete = true;

    if (this.initialSpawnQueue.size === 0) {
      // console.log('🎯 Initial spawn: no circles queued');
      return;
    }

    // 1. Sort queued circles by activity (gas in descending order)
    const sorted = [...this.initialSpawnQueue.entries()]
      .sort((a, b) => b[1].gas - a[1].gas);

    // Limit to MAX_CIRCLES to avoid overwhelming the screen
    const toSpawn = sorted.slice(0, this.MAX_CIRCLES);
    // console.log(`🎯 Initial spawn: positioning ${toSpawn.length} circles by activity`);

    // 2. Calculate spawn zones for even distribution
    const zones = this.calculateSpawnZones(toSpawn.length);

    // 3. Place each circle in its assigned zone
    for (let i = 0; i < toSpawn.length; i++) {
      const [circleKey, activity] = toSpawn[i];

      // Skip if circle already exists (might have been created via saved position)
      if (this.circleKeyToCircle.has(circleKey)) continue;

      // Skip if deleted
      const addresses = getAddressesForCircleKey(circleKey);
      const isDeleted = addresses.some(addr => userSettings.isCircleDeleted(addr));
      if (isDeleted) continue;

      // Get zone for this circle (zones are shuffled so distribution looks natural)
      const zone = zones[i % zones.length];

      // Find valid position within zone
      const position = this.findPositionInZone(zone.x, zone.y, zone.width, zone.height, this.BASE_RADIUS);

      if (position) {
        // Create circle at specific position
        const circle = this.createCircleAtPosition(circleKey, position.x, position.y);
        if (circle) {
          // Apply initial stats from pending data
          for (const addr of circle.addresses) {
            const pending = this.pendingAddresses.get(addr);
            if (pending) {
              circle.txCount += pending.txCount;
              circle.gasUsed += pending.gasUsed;
              circle.lastMinuteCount += pending.txCount;
              this.pendingAddresses.delete(addr);
            }
          }
          circle.tps = circle.lastMinuteCount / 60;
        }
      }
    }

    // Clear the spawn queue
    this.initialSpawnQueue.clear();

    // Force ranking recalculation to size circles correctly
    this.lastRankingUpdate = 0;
  }

  /**
   * Calculate spawn zones for even distribution of circles
   * Top activity circles get center/prominent positions, rest are distributed
   */
  private calculateSpawnZones(circleCount: number): Array<{ x: number; y: number; width: number; height: number }> {
    if (circleCount === 0) return [];

    // Calculate grid dimensions based on circle count
    const cols = Math.max(1, Math.ceil(Math.sqrt(circleCount * 1.5))); // Slightly wider than square
    const rows = Math.max(1, Math.ceil(circleCount / cols));

    const usableHeight = this.MAX_Y - this.MIN_Y;
    const zoneWidth = this.SCENE_WIDTH / cols;
    const zoneHeight = usableHeight / rows;

    const allZones: Array<{ x: number; y: number; width: number; height: number; distFromCenter: number }> = [];

    // Calculate center of the spawn area
    const centerX = this.SCENE_WIDTH / 2;
    const centerY = this.MIN_Y + usableHeight / 2;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = col * zoneWidth + zoneWidth / 2;
        const y = this.MIN_Y + row * zoneHeight + zoneHeight / 2;

        // Calculate distance from center (for sorting)
        const dx = x - centerX;
        const dy = y - centerY;
        const distFromCenter = Math.sqrt(dx * dx + dy * dy);

        allZones.push({ x, y, width: zoneWidth, height: zoneHeight, distFromCenter });
      }
    }

    // Sort zones by distance from center (closest first)
    allZones.sort((a, b) => a.distFromCenter - b.distFromCenter);

    // Top 5 most active circles get the most central positions (no shuffle)
    // Remaining zones get shuffled for natural distribution
    const topCount = Math.min(5, allZones.length);
    const topZones = allZones.slice(0, topCount);
    const remainingZones = allZones.slice(topCount);

    // Shuffle only the remaining zones
    for (let i = remainingZones.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [remainingZones[i], remainingZones[j]] = [remainingZones[j], remainingZones[i]];
    }

    // Return combined: top zones first (for most active), then shuffled rest
    return [...topZones, ...remainingZones].map(z => ({
      x: z.x,
      y: z.y,
      width: z.width,
      height: z.height
    }));
  }

  /**
   * Find a valid position within a zone
   * Starts from zone center and spirals outward
   */
  private findPositionInZone(
    zoneX: number,
    zoneY: number,
    zoneWidth: number,
    zoneHeight: number,
    radius: number
  ): { x: number; y: number } | null {
    // Try zone center first
    if (this.isValidPosition(zoneX, zoneY, radius)) {
      return { x: zoneX, y: zoneY };
    }

    // Spiral search within zone bounds
    const maxSearchRadius = Math.min(zoneWidth, zoneHeight) / 2;
    const step = radius * 2 + this.MIN_CIRCLE_GAP;

    for (let searchRadius = step; searchRadius <= maxSearchRadius; searchRadius += step) {
      const points = Math.max(8, Math.floor(searchRadius / 10));

      for (let i = 0; i < points; i++) {
        const angle = (i / points) * Math.PI * 2;
        const x = zoneX + Math.cos(angle) * searchRadius;
        const y = zoneY + Math.sin(angle) * searchRadius;

        // Stay within zone bounds (with some margin)
        const margin = radius + this.MIN_CIRCLE_GAP;
        if (x < zoneX - zoneWidth / 2 + margin || x > zoneX + zoneWidth / 2 - margin) continue;
        if (y < zoneY - zoneHeight / 2 + margin || y > zoneY + zoneHeight / 2 - margin) continue;

        if (this.isValidPosition(x, y, radius)) {
          return { x, y };
        }
      }
    }

    // Fallback to standard spiral from zone center (may go outside zone)
    return this.findValidPositionSpiral(zoneX, zoneY, radius);
  }

  /**
   * Create a circle at a specific position (bypasses random position finding)
   */
  private createCircleAtPosition(circleKey: string, x: number, y: number): CircleData | null {
    // IMPORTANT: Check if circle already exists for this key (prevent duplicates)
    const existingCircle = this.circleKeyToCircle.get(circleKey);
    if (existingCircle) {
      return existingCircle;
    }

    // Get all addresses for this circle key
    const addresses = getAddressesForCircleKey(circleKey);
    const primaryAddress = getPrimaryAddressForCircleKey(circleKey);

    // Check if position is saved (shouldn't be, but check anyway)
    let isSaved = false;
    for (const addr of addresses) {
      if (userSettings.getSavedCirclePosition(addr)) {
        isSaved = true;
        break;
      }
    }

    // Determine circle color based on saved state
    const circleColor = isSaved ? SAVED_CIRCLE_COLOR : BLOCK_COLOR;

    // Create circle data
    const circle: CircleData = {
      circleKey,
      address: primaryAddress,
      addresses,
      x,
      y,
      radius: this.BASE_RADIUS,
      baseRadius: this.BASE_RADIUS,
      maxRadius: this.MAX_RADIUS,
      targetRadius: this.BASE_RADIUS,
      txCount: 0,
      gasUsed: 0,
      lastActivityTime: performance.now(),
      graphics: null,
      sprite: null,
      hasImage: false,
      imageUrl: null,
      projectData: null,
      lastMinuteCount: 0,
      tps: 0,
      velocityX: 0,
      velocityY: 0,
      isSaved,
      restoredFromStorage: false,
      highlightGraphics: null,
    };

    // Create Graphics object for rendering
    const graphics = new Graphics();
    graphics.circle(0, 0, this.BASE_RADIUS);
    graphics.fill({ color: circleColor, alpha: 0.01 });
    graphics.stroke({ width: 1, color: circleColor });
    graphics.x = x;
    graphics.y = y;

    // Make graphics interactive and draggable
    graphics.eventMode = 'static';
    graphics.cursor = 'grab';
    const baseR = this.BASE_RADIUS;
    graphics.hitArea = { contains: (hx: number, hy: number) => hx * hx + hy * hy <= baseR * baseR };

    // Drag handlers
    this.setupDragHandlers(graphics, circle, primaryAddress);

    this.container.addChild(graphics);
    circle.graphics = graphics;

    // Add to maps
    this.circleKeyToCircle.set(circleKey, circle);
    for (const addr of addresses) {
      this.addressToCircleKey.set(addr, circleKey);
    }
    this.addToGrid(circle);

    // Load image for known dapps
    let project: ReturnType<typeof getProjectByAddress> = undefined;
    if (isProjectKey(circleKey)) {
      const projectName = getProjectNameFromKey(circleKey);
      if (projectName) {
        project = getProjectByName(projectName);
      }
    } else {
      project = getProjectByAddress(primaryAddress);
    }

    if (project && project.img) {
      this.loadImageForCircle(circle, circleKey, project);
    }

    return circle;
  }

  /**
   * Enter testing mode - fill screen with circles to simulate high traffic
   * Creates circles with varied sizes using power law distribution
   */
  public enterTestingMode(): void {
    if (this.isTestingMode) return; // Already in testing mode

    this.isTestingMode = true;
    // console.log('🧪 Entering testing mode - populating circles...');

    // Target: fill to MAX_CIRCLES (100) with varied sizes
    const targetCircles = this.MAX_CIRCLES;
    const circlesToCreate = targetCircles - this.circleKeyToCircle.size;

    // Generate fake addresses with realistic activity distributions
    // Use power law: few high-activity addresses, many low-activity addresses
    for (let i = 0; i < circlesToCreate; i++) {
      const testAddress = `0xTEST${i.toString().padStart(10, '0')}`;
      this.testingModeAddresses.add(testAddress);

      // Power law distribution for activity levels (dev testing mode only)
      // Higher index = lower activity (ranks start at 0)
      const normalizedRank = i / (circlesToCreate - 1 || 1);
      const activityFactor = Math.pow(1 - normalizedRank, 2.5);

      // Generate realistic metrics based on chart mode
      let txCount: number;
      let gasUsed: number;

      if (this.chartMode === 'gas') {
        // Gas mode: vary gas from 50M (high) to 100k (low)
        gasUsed = Math.floor(100000 + activityFactor * 49900000);
        txCount = Math.floor(gasUsed / 21000); // Approximate tx count from gas
      } else {
        // TxCount mode: vary from 10000 (high) to 10 (low)
        txCount = Math.floor(10 + activityFactor * 9990);
        gasUsed = txCount * 21000; // Approximate gas from tx count
      }

      // Create the circle immediately (bypass threshold for testing)
      // Test addresses use themselves as circle key (no project mapping)
      const circle = this.createCircleForKey(testAddress);

      if (circle) {
        // Set metrics to create varied sizes
        circle.txCount = txCount;
        circle.gasUsed = gasUsed;
        circle.lastActivityTime = performance.now();
      }
    }

    // console.log(`🧪 Testing mode active: ${this.circleKeyToCircle.size} circles (${this.testingModeAddresses.size} test addresses)`);

    // Trigger immediate ranking update to size circles correctly
    this.lastRankingUpdate = 0; // Force ranking recalc on next update
  }

  /**
   * Exit testing mode - remove all test circles and return to production behavior
   */
  public exitTestingMode(): void {
    if (!this.isTestingMode) return; // Not in testing mode

    // console.log('🧪 Exiting testing mode - removing test circles...');

    // Remove all test addresses (test addresses use themselves as circle keys)
    let removedCount = 0;
    for (const testAddress of this.testingModeAddresses) {
      if (this.circleKeyToCircle.has(testAddress)) {
        this.removeCircleByKey(testAddress);
        removedCount++;
      }
    }

    // Clear test address tracking
    this.testingModeAddresses.clear();
    this.isTestingMode = false;

    // console.log(`🧪 Testing mode exited: removed ${removedCount} test circles (${this.circleKeyToCircle.size} circles remaining)`);
  }

  /**
   * Update world bounds when viewport is resized
   * Recalculates vertical constraints for circle positioning
   */
  public updateBounds(worldWidth: number, worldHeight: number): void {
    this.SCENE_WIDTH = worldWidth;
    this.SCENE_HEIGHT = worldHeight;

    // Recalculate vertical constraints
    this.MAX_Y = this.SCENE_HEIGHT; // Spawn to bottom edge (circles can extend past bottom)

    // console.log(`🔵 Bounds updated: world ${worldWidth}x${worldHeight}, circle area Y=${this.MIN_Y}-${this.MAX_Y}`);
  }

  /**
   * Reorganize all circles to fit in new world bounds
   * Called after debounced resize completes
   */
  public reorganizeAllCircles(): void {
    // console.log(`🔵 Reorganizing ${this.circleKeyToCircle.size} circles for new bounds...`);

    // Clear spatial grid (will be rebuilt as we reposition)
    this.spatialGrid.clear();

    const circles = Array.from(this.circleKeyToCircle.values());
    let successCount = 0;
    let failureCount = 0;

    // Reposition each circle using spiral search
    for (const circle of circles) {
      // Remove from current position
      this.removeFromGrid(circle);

      // Try to find new valid position near current location
      let newPosition = this.findValidPositionSpiral(circle.x, circle.y, circle.radius);

      // If that fails, try random position
      if (!newPosition) {
        const randomX = Math.random() * this.SCENE_WIDTH;
        const randomY = this.MIN_Y + Math.random() * (this.MAX_Y - this.MIN_Y);
        newPosition = this.findValidPositionSpiral(randomX, randomY, circle.radius);
      }

      if (newPosition) {
        // Update circle position
        circle.x = newPosition.x;
        circle.y = newPosition.y;

        // Update graphics position
        if (circle.graphics) {
          circle.graphics.x = newPosition.x;
          circle.graphics.y = newPosition.y;
        }

        // Update sprite/mask position
        if (circle.sprite) {
          circle.sprite.x = newPosition.x;
          circle.sprite.y = newPosition.y;

          if (circle.sprite.mask && circle.sprite.mask instanceof Graphics) {
            (circle.sprite.mask as Graphics).clear();
            (circle.sprite.mask as Graphics).circle(newPosition.x, newPosition.y, circle.radius);
            (circle.sprite.mask as Graphics).fill({ color: 0xFFFFFF });
          }
        }

        // Add back to spatial grid
        this.addToGrid(circle);
        successCount++;
      } else {
        // Failed to find position - keep at current location and add to grid anyway
        this.addToGrid(circle);
        failureCount++;
      }
    }

    // console.log(`🔵 Reorganization complete: ${successCount} repositioned, ${failureCount} kept in place`);
  }

  /**
   * Get random off-screen position for transactions from pending/deleted addresses
   * Spawns from random points along the left edge, right edge, or bottom edge
   * within the circle spawn Y area (MIN_Y to MAX_Y)
   */
  private getOffScreenPosition(): { x: number; y: number; radius: number } {
    // Choose which edge to spawn from: 0 = left, 1 = right, 2 = bottom
    const edge = Math.floor(Math.random() * 3);

    let x: number;
    let y: number;

    if (edge === 0) {
      // Left edge - random Y within circle area
      x = -30;
      y = this.MIN_Y + Math.random() * (this.MAX_Y - this.MIN_Y);
    } else if (edge === 1) {
      // Right edge - random Y within circle area
      x = this.SCENE_WIDTH + 30;
      y = this.MIN_Y + Math.random() * (this.MAX_Y - this.MIN_Y);
    } else {
      // Bottom edge - random X across the scene width
      x = Math.random() * this.SCENE_WIDTH;
      y = this.MAX_Y + 50;
    }

    return { x, y, radius: this.OFF_SCREEN_RADIUS };
  }

  /**
   * Calculate dynamic threshold based on demand (competitive admission system)
   * No fixed cap - threshold grows infinitely with traffic volume
   * Returns minimum transactions needed to get a circle
   */
  private calculateDynamicThreshold(): number {
    const currentCircles = this.circleKeyToCircle.size;
    const availableSlots = this.MAX_CIRCLES - currentCircles;
    const pendingCount = this.pendingAddresses.size;

    // Case 1: Plenty of space - accept anyone
    if (pendingCount <= availableSlots) {
      return 1; // Low barrier, create circles freely
    }

    // Case 2: Competition for slots - use competitive cutoff
    // Sort pending addresses by activity (highest first)
    const sortedPending = Array.from(this.pendingAddresses.entries())
      .map(([address, data]) => ({
        address,
        txCount: data.txCount,
        gasUsed: data.gasUsed
      }))
      .sort((a, b) => {
        // Sort by current chart mode metric
        const metricA = this.chartMode === 'gas' ? a.gasUsed : a.txCount;
        const metricB = this.chartMode === 'gas' ? b.gasUsed : b.txCount;
        return metricB - metricA;
      });

    // Threshold = activity of the Nth pending address (where N = available slots)
    // This is the "cutoff line" - you need this much activity to compete
    const cutoffIndex = Math.max(0, availableSlots - 1);
    const cutoffPending = sortedPending[cutoffIndex];

    if (cutoffPending) {
      const cutoffMetric = this.chartMode === 'gas'
        ? cutoffPending.gasUsed
        : cutoffPending.txCount;

      // Threshold is slightly above cutoff to prevent thrashing
      // Add 10% buffer to avoid constant promotion/demotion
      return Math.ceil(cutoffMetric * 1.1);
    }

    return 1; // Fallback
  }

  /**
   * Get or create a circle for a specific address
   * Returns the circle data for spawning transactions
   * @param address - The transaction "to" address
   * @param gasUsed - Gas used by this transaction (optional, defaults to 0)
   */
  public getOrCreateCircleForAddress(address: string, gasUsed: number = 0): { x: number; y: number; radius: number } {
    // 0. Check if this address is deleted - always spawn from off-screen
    if (userSettings.isCircleDeleted(address)) {
      return this.getOffScreenPosition();
    }

    // 1. Check if circle already exists (already promoted)
    // Use circle key lookup to handle multi-contract dapps
    let circle = this.getCircleByAddress(address);
    if (circle) {
      // Update last activity time (used for inactivity timeout)
      circle.lastActivityTime = performance.now();

      // NOTE: Stats (txCount, gasUsed, tps, lastMinuteCount) are updated by backend via updateFromStatsUpdate()
      // We don't track stats locally - backend is the single source of truth

      return { x: circle.x, y: circle.y, radius: circle.radius };
    }

    // 1.5. During initial spawn delay, queue unsaved circles instead of creating immediately
    // This allows us to collect activity data and position circles smartly
    const circleKey = getCircleKeyForAddress(address);
    if (!this.initialSpawnComplete && !this.hasSavedPosition(circleKey)) {
      // Queue activity data for smart initial spawn
      const existing = this.initialSpawnQueue.get(circleKey) || { gas: 0, tx: 0 };
      this.initialSpawnQueue.set(circleKey, {
        gas: existing.gas + gasUsed,
        tx: existing.tx + 1
      });

      // Also track in pending for stats
      let pending = this.pendingAddresses.get(address);
      if (!pending) {
        pending = {
          txCount: 0,
          gasUsed: 0,
          firstSeenTime: performance.now(),
          lastActivityTime: performance.now()
        };
        this.pendingAddresses.set(address, pending);
      }
      pending.txCount++;
      pending.gasUsed += gasUsed;
      pending.lastActivityTime = performance.now();

      // Return off-screen position until initial spawn completes
      return this.getOffScreenPosition();
    }

    // 2. Check/create pending entry
    let pending = this.pendingAddresses.get(address);
    if (!pending) {
      // Enforce hard cap on pending addresses to prevent memory growth during spam
      const MAX_PENDING_ADDRESSES = 10000;
      if (this.pendingAddresses.size >= MAX_PENDING_ADDRESSES) {
        // At capacity - skip tracking new address, just spawn off-screen
        return this.getOffScreenPosition();
      }

      // New address - create pending entry
      pending = {
        txCount: 0,
        gasUsed: 0,
        firstSeenTime: performance.now(),
        lastActivityTime: performance.now()
      };
      this.pendingAddresses.set(address, pending);
    }

    // 3. Update pending metrics
    pending.txCount++;
    pending.gasUsed += gasUsed;
    pending.lastActivityTime = performance.now();

    // 4. Determine if we should create a circle
    // In production mode (not testing) with few circles, bypass threshold for faster startup
    const currentCircleCount = this.circleKeyToCircle.size;
    const shouldBypassThreshold = !this.isTestingMode && currentCircleCount < 20;

    const metric = this.chartMode === 'gas' ? pending.gasUsed : pending.txCount;
    const shouldPromote = shouldBypassThreshold || metric >= this.dynamicThreshold;

    if (shouldPromote) {
      // Attempt promotion to actual circle
      // Use circle key to consolidate multi-contract dapps
      const newCircle = this.createCircleForKey(circleKey);

      if (newCircle) {
        // Success! Remove from pending map all addresses for this circle
        for (const addr of newCircle.addresses) {
          this.pendingAddresses.delete(addr);
        }

        return { x: newCircle.x, y: newCircle.y, radius: newCircle.radius };
      }
      // If placement fails, continue to off-screen spawn
    }

    // 5. Below threshold or failed placement - spawn from off-screen
    return this.getOffScreenPosition();
  }

  /**
   * Create a new circle for a circle key at a valid position
   * Uses spiral packing algorithm to find nearby valid positions
   * Handles both project keys (multi-contract dapps) and raw addresses
   * @param circleKey - Circle key: "project:{name}" or raw address
   * @param targetRadius - Optional target radius for positioning (defaults to BASE_RADIUS)
   */
  private createCircleForKey(circleKey: string, targetRadius: number = this.BASE_RADIUS): CircleData | null {

    // IMPORTANT: Check if circle already exists for this key (prevent duplicates)
    const existingCircle = this.circleKeyToCircle.get(circleKey);
    if (existingCircle) {
      return existingCircle;
    }

    // Get all addresses for this circle key
    const addresses = getAddressesForCircleKey(circleKey);
    const primaryAddress = getPrimaryAddressForCircleKey(circleKey);


    // IMPORTANT: Check if any of these addresses already have a circle with a DIFFERENT key
    // This handles the case where circles were created with raw address keys before OLI labels loaded
    // We need to find ALL existing circles and merge them into one
    let targetCircle: CircleData | null = null;
    const circlesToMerge: Array<{ key: string; circle: CircleData }> = [];

    for (const addr of addresses) {
      const existingKey = this.addressToCircleKey.get(addr);
      if (existingKey && existingKey !== circleKey) {
        const oldCircle = this.circleKeyToCircle.get(existingKey);
        if (oldCircle) {
          // Check if we already found this circle (avoid duplicates in merge list)
          const alreadyFound = circlesToMerge.some(m => m.circle === oldCircle);
          if (!alreadyFound) {
            circlesToMerge.push({ key: existingKey, circle: oldCircle });
          }
        }
      }
    }

    // If we found existing circles, merge them all into one
    if (circlesToMerge.length > 0) {
      // Use the first one as the target (or the one with a saved position)
      targetCircle = circlesToMerge[0].circle;
      for (const { circle } of circlesToMerge) {
        if (circle.isSaved) {
          targetCircle = circle;
          break;
        }
      }

      // Merge all other circles into the target
      for (const { key, circle } of circlesToMerge) {
        if (circle === targetCircle) {
          // Just update the key for target circle
          this.circleKeyToCircle.delete(key);
        } else {
          // Merge stats and addresses into target, then remove
          targetCircle.txCount += circle.txCount;
          targetCircle.gasUsed += circle.gasUsed;
          targetCircle.lastMinuteCount += circle.lastMinuteCount;
          targetCircle.tps += circle.tps;
          if (circle.isSaved) targetCircle.isSaved = true;

          // Add addresses from merged circle
          for (const addr of circle.addresses) {
            if (!targetCircle.addresses.includes(addr)) {
              targetCircle.addresses.push(addr);
            }
          }

          // Remove the merged circle
          this.circleKeyToCircle.delete(key);
          this.removeFromGrid(circle);
          if (circle.graphics) {
            this.container.removeChild(circle.graphics);
            circle.graphics.destroy();
          }
          if (circle.sprite) {
            this.container.removeChild(circle.sprite);
            circle.sprite.destroy();
          }
        }
      }

      // Set up the target circle with new key
      this.circleKeyToCircle.set(circleKey, targetCircle);
      targetCircle.circleKey = circleKey;

      // Update all address mappings
      for (const a of targetCircle.addresses) {
        this.addressToCircleKey.set(a, circleKey);
      }
      // Add any new addresses not yet in the circle
      for (const a of addresses) {
        if (!targetCircle.addresses.includes(a)) {
          targetCircle.addresses.push(a);
        }
        this.addressToCircleKey.set(a, circleKey);
      }

      // Load project image if we now have one
      if (isProjectKey(circleKey) && !targetCircle.sprite) {
        const projectName = getProjectNameFromKey(circleKey);
        if (projectName) {
          const project = getProjectByName(projectName);
          if (project?.img) {
            this.loadImageForCircle(targetCircle, circleKey, project);
          }
        }
      }

      return targetCircle;
    }

    // Check if any of the addresses has a saved position
    let savedPos: { x: number; y: number } | null = null;
    let isSaved = false;
    for (const addr of addresses) {
      const pos = userSettings.getSavedCirclePosition(addr);
      if (pos) {
        savedPos = { x: pos.x, y: pos.y };
        isSaved = true;
        break;
      }
    }

    let x: number, y: number;

    if (savedPos) {
      // Use saved position
      x = savedPos.x;
      y = savedPos.y;
    } else {
      // Start from a random base position in the valid area
      const baseX = Math.random() * this.SCENE_WIDTH;
      const baseY = this.MIN_Y + Math.random() * (this.MAX_Y - this.MIN_Y);

      // Try spiral search pattern to find valid position
      const position = this.findValidPositionSpiral(baseX, baseY, targetRadius);

      if (!position) return null;

      x = position.x;
      y = position.y;
    }

    // Determine circle color based on saved state
    const circleColor = isSaved ? SAVED_CIRCLE_COLOR : BLOCK_COLOR;

    // Create circle data
    const circle: CircleData = {
      circleKey,
      address: primaryAddress, // Primary address for backwards compatibility
      addresses,
      x,
      y,
      radius: this.BASE_RADIUS, // Start at base radius
      baseRadius: this.BASE_RADIUS,
      maxRadius: this.MAX_RADIUS,
      targetRadius: targetRadius,
      txCount: 0,
      gasUsed: 0,
      lastActivityTime: performance.now(),
      graphics: null,
      sprite: null,
      hasImage: false,
      imageUrl: null,
      projectData: null,

      // Stats (initialized from backend, incremented on new tx)
      lastMinuteCount: 0,
      tps: 0,

      // Initialize drag physics
      velocityX: 0,
      velocityY: 0,

      // Saved state
      isSaved,
      restoredFromStorage: false, // Set to true by setInitialState for restored circles
      highlightGraphics: null,
    };

    // Create Graphics object for rendering
    const graphics = new Graphics();
    graphics.circle(0, 0, this.BASE_RADIUS);
    graphics.fill({ color: circleColor, alpha: 0.01 }); // Transparent fill for clickability
    graphics.stroke({ width: 1, color: circleColor });
    graphics.x = x;
    graphics.y = y;

    // Make graphics interactive and draggable
    graphics.eventMode = 'static';
    graphics.cursor = 'grab';
    // OPTIMIZATION: Use contains function instead of new Graphics()
    const baseR = this.BASE_RADIUS;
    graphics.hitArea = { contains: (hx: number, hy: number) => hx * hx + hy * hy <= baseR * baseR };

    // Drag handlers - use primary address for callbacks
    this.setupDragHandlers(graphics, circle, primaryAddress);

    this.container.addChild(graphics);
    circle.graphics = graphics;

    // Add to maps
    this.circleKeyToCircle.set(circleKey, circle);
    // Also add reverse lookups for all addresses
    for (const addr of addresses) {
      this.addressToCircleKey.set(addr, circleKey);
    }
    this.addToGrid(circle);

    // Load image immediately for known dapps (don't wait for it to become largest)
    // For project keys (e.g., "project:redstone-finance"), look up by project name
    // For raw addresses, look up via OLI labels
    let project: ReturnType<typeof getProjectByAddress> = undefined;
    if (isProjectKey(circleKey)) {
      const projectName = getProjectNameFromKey(circleKey);
      if (projectName) {
        project = getProjectByName(projectName);
      }
    } else {
      project = getProjectByAddress(primaryAddress);
    }

    if (project && project.img) {
      this.loadImageForCircle(circle, circleKey, project);
    }

    return circle;
  }

  /**
   * Find a valid non-overlapping position using spiral search pattern
   * Searches in concentric circles around the base position
   */
  private findValidPositionSpiral(
    baseX: number,
    baseY: number,
    radius: number
  ): { x: number; y: number } | null {
    // Try the base position first
    if (this.isValidPosition(baseX, baseY, radius)) {
      return { x: baseX, y: baseY };
    }

    // Spiral search: check positions in expanding rings
    const maxRadius = 200; // Maximum search distance from base
    const step = radius * 2 + this.MIN_CIRCLE_GAP; // Step size based on circle size

    for (let searchRadius = step; searchRadius <= maxRadius; searchRadius += step) {
      // Try 16 positions around the ring (more points for larger rings)
      const points = Math.max(8, Math.floor(searchRadius / 10));

      for (let i = 0; i < points; i++) {
        const angle = (i / points) * Math.PI * 2;
        const x = baseX + Math.cos(angle) * searchRadius;
        const y = baseY + Math.sin(angle) * searchRadius;

        if (this.isValidPosition(x, y, radius)) {
          return { x, y };
        }
      }
    }

    // If spiral search fails, try pure random as fallback
    for (let i = 0; i < 50; i++) {
      const x = Math.random() * this.SCENE_WIDTH;
      const y = this.MIN_Y + Math.random() * (this.MAX_Y - this.MIN_Y);

      if (this.isValidPosition(x, y, radius)) {
        return { x, y };
      }
    }

    return null; // No valid position found
  }

  /**
   * Check if a position is valid for placing a circle
   * - Must be within scene bounds
   * - Must be below sequencer bottom + padding
   * - Must not overlap with existing circles (with gap)
   */
  private isValidPosition(x: number, y: number, radius: number): boolean {
    // Check horizontal bounds (keep circle fully in scene)
    if (x - radius < 0 || x + radius > this.SCENE_WIDTH) {
      return false;
    }

    // Check vertical bounds
    // Top: circle top must be below sequencer bottom + 10px padding
    const circleTop = y - radius;
    if (circleTop < this.SEQUENCER_BOTTOM_Y + 10) {
      return false;
    }

    // Bottom: circle bottom must be above max Y
    const circleBottom = y + radius;
    if (circleBottom > this.MAX_Y) {
      return false;
    }

    // Check for overlaps with existing circles (includes gap)
    if (this.checkOverlap(x, y, radius)) {
      return false;
    }

    return true;
  }

  /**
   * Get grid key for a position
   */
  private getGridKey(x: number, y: number): string {
    const gridX = Math.floor(x / this.GRID_CELL_SIZE);
    const gridY = Math.floor(y / this.GRID_CELL_SIZE);
    return `${gridX},${gridY}`;
  }

  /**
   * Add circle to spatial grid
   */
  private addToGrid(circle: CircleData): void {
    const key = this.getGridKey(circle.x, circle.y);
    if (!this.spatialGrid.has(key)) {
      this.spatialGrid.set(key, []);
    }
    this.spatialGrid.get(key)!.push(circle);
  }

  /**
   * Remove circle from spatial grid using swap-with-last (O(1) removal)
   */
  private removeFromGrid(circle: CircleData): void {
    const key = this.getGridKey(circle.x, circle.y);
    const cell = this.spatialGrid.get(key);
    if (cell) {
      const index = cell.indexOf(circle);
      if (index !== -1) {
        // Swap-with-last removal (O(1) instead of O(n) splice)
        const lastIndex = cell.length - 1;
        if (index !== lastIndex) {
          cell[index] = cell[lastIndex];
        }
        cell.pop();
      }
      // Clean up empty cells
      if (cell.length === 0) {
        this.spatialGrid.delete(key);
      }
    }
  }

  /**
   * Initialize (no-op for address-based system - circles created on-demand)
   */
  public start(): void {
    // console.log('🔵 Circle system started (address-based)');
  }


  /**
   * Update circles from backend-calculated stats (NEW ARCHITECTURE)
   * Backend provides ranked list with pre-calculated TPS - frontend just displays
   * This replaces local time-windowed calculation and ranking
   *
   * Circle limit logic:
   * - Saved circles are ALWAYS shown (no limit)
   * - Non-saved circles are limited to MAX_CIRCLES (top 20 by activity)
   * - Deleted circles are never shown
   */
  public updateFromStatsUpdate(projects: Array<{ addr: string; gas: number; txc: number; tps: number }>): void {
    // STEP 1: Aggregate stats by circle key (consolidates multi-contract dapps)
    // Each circleKey maps to aggregated stats from all its addresses
    const aggregatedByKey = new Map<string, { gas: number; txc: number; tps: number; addresses: string[] }>();

    // Cache deleted addresses set for O(1) lookup
    const deletedAddresses = userSettings.deletedCircles;
    const savedAddresses = userSettings.savedCircles;

    for (const project of projects) {
      // Skip deleted addresses
      if (deletedAddresses[project.addr]) continue;

      // Get circle key for this address (project key for known dapps, or raw address)
      const circleKey = getCircleKeyForAddress(project.addr);

      // Aggregate stats
      const existing = aggregatedByKey.get(circleKey);
      if (existing) {
        existing.gas += project.gas;
        existing.txc += project.txc;
        existing.tps += project.tps;
        existing.addresses.push(project.addr);
      } else {
        aggregatedByKey.set(circleKey, {
          gas: project.gas,
          txc: project.txc,
          tps: project.tps,
          addresses: [project.addr]
        });
      }
    }

    // STEP 2: Select which circle keys get circles
    // Saved circle keys always get a circle; non-saved limited to MAX_CIRCLES
    const circleData = new Map<string, { gas: number; txc: number; tps: number; addresses: string[] }>();

    // First, collect saved circle keys (check if any address is saved)
    const savedCircleKeys = new Set<string>();
    for (const address of Object.keys(savedAddresses)) {
      if (!deletedAddresses[address]) {
        const circleKey = getCircleKeyForAddress(address);
        savedCircleKeys.add(circleKey);
      }
    }

    // Sort aggregated data by gas (descending) for top selection
    const sortedByGas = Array.from(aggregatedByKey.entries())
      .sort((a, b) => b[1].gas - a[1].gas);

    let nonSavedCount = 0;
    for (const [circleKey, data] of sortedByGas) {
      const isSaved = savedCircleKeys.has(circleKey);

      if (isSaved) {
        // Saved circles always included
        circleData.set(circleKey, data);
      } else if (nonSavedCount < this.MAX_CIRCLES) {
        // Non-saved: only top MAX_CIRCLES
        circleData.set(circleKey, data);
        nonSavedCount++;
      }

      // Early exit once we have enough
      if (nonSavedCount >= this.MAX_CIRCLES && circleData.size > this.MAX_CIRCLES + savedCircleKeys.size) {
        break;
      }
    }

    // STEP 3: Remove circles no longer in valid set (but keep saved circles)
    const toRemove: string[] = [];
    for (const [circleKey, circle] of this.circleKeyToCircle) {
      if (circle.isSaved) continue; // Never auto-remove saved
      if (!circleData.has(circleKey)) {
        toRemove.push(circleKey);
      }
    }
    for (const circleKey of toRemove) {
      this.removeCircleByKey(circleKey);
    }

    // STEP 4: Update or create circles with aggregated data
    for (const [circleKey, data] of circleData) {
      let circle = this.circleKeyToCircle.get(circleKey);

      if (!circle) {
        const newCircle = this.createCircleForKey(circleKey);
        if (!newCircle) continue;
        circle = newCircle;
      }

      // Ensure saved circles are marked correctly (belt and suspenders check)
      if (savedCircleKeys.has(circleKey) && !circle.isSaved) {
        circle.isSaved = true;
        // Redraw with saved color
        if (circle.graphics) {
          circle.graphics.clear();
          circle.graphics.circle(0, 0, circle.radius);
          circle.graphics.fill({ color: SAVED_CIRCLE_COLOR, alpha: 0.01 });
          circle.graphics.stroke({ width: 1, color: SAVED_CIRCLE_COLOR });
        }
      }

      // Update metrics from aggregated backend data
      circle.txCount = data.txc;
      circle.gasUsed = data.gas;
      circle.tps = data.tps;
      circle.lastMinuteCount = data.txc;
    }

    // STEP 5: Create circles for saved addresses not in API response (0 recent activity)
    for (const circleKey of savedCircleKeys) {
      if (this.circleKeyToCircle.has(circleKey)) continue;

      const newCircle = this.createCircleForKey(circleKey);
      if (newCircle) {
        newCircle.txCount = 0;
        newCircle.gasUsed = 0;
        newCircle.tps = 0;
        newCircle.lastMinuteCount = 0;
      }
    }
  }

  /**
   * Update circles from locally-calculated stats (frontend rolling window)
   * Similar to updateFromStatsUpdate but takes a Map format from local aggregation
   */
  public updateFromLocalStats(aggregatedAddresses: Map<string, { txc: number; gas: number }>): void {
    // Convert Map to array format and call existing method
    // Calculate TPS as txc / 60 (60-second window)
    const projects: Array<{ addr: string; gas: number; txc: number; tps: number }> = [];

    for (const [addr, data] of aggregatedAddresses) {
      projects.push({
        addr,
        gas: data.gas,
        txc: data.txc,
        tps: data.txc / 60 // Calculate TPS from 60-second window
      });
    }

    // Reuse existing logic
    this.updateFromStatsUpdate(projects);
  }

  /**
   * Calculate maximum safe radius for a circle at its current position
   * Checks all nearby circles and returns the largest radius that won't cause overlap
   * OPTIMIZATION: Uses squared distance to avoid expensive Math.sqrt calls
   */
  private getMaxSafeRadius(circle: CircleData): number {
    const gridX = Math.floor(circle.x / this.GRID_CELL_SIZE);
    const gridY = Math.floor(circle.y / this.GRID_CELL_SIZE);

    let maxRadius = this.MAX_RADIUS; // Start with maximum possible

    // Check all 9 adjacent grid cells
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${gridX + dx},${gridY + dy}`;
        const cell = this.spatialGrid.get(key);

        if (!cell) continue;

        // Check distance to each nearby circle
        for (const otherCircle of cell) {
          if (otherCircle === circle) continue; // Skip self

          const distX = circle.x - otherCircle.x;
          const distY = circle.y - otherCircle.y;
          const distSq = distX * distX + distY * distY;

          // OPTIMIZATION: Only calculate sqrt when the circles could potentially overlap
          // If distSq > (maxRadius + otherRadius + gap)^2, no need to check further
          const maxPossibleDist = maxRadius + otherCircle.radius + this.MIN_CIRCLE_GAP;
          if (distSq >= maxPossibleDist * maxPossibleDist) continue;

          // Now we need the actual distance
          const dist = Math.sqrt(distSq);

          // Calculate max radius that won't overlap with this circle
          // dist = r1 + r2 + gap  =>  r1 = dist - r2 - gap
          const maxRadiusForThisCircle = dist - otherCircle.radius - this.MIN_CIRCLE_GAP;

          // Take the most restrictive limit
          if (maxRadiusForThisCircle < maxRadius) {
            maxRadius = maxRadiusForThisCircle;
          }
        }
      }
    }

    // Ensure radius is at least BASE_RADIUS
    return Math.max(this.BASE_RADIUS, maxRadius);
  }

  /**
   * Update all circles: rank by activity and assign sizes based on ranking
   * Called every frame, but ranking recalculation is throttled to 100ms intervals
   */
  public update(deltaTime: number): void {
    const now = performance.now();
    let largestCircle: { circle: CircleData; circleKey: string } | null = null;

    // Throttled ranking recalculation (only every 100ms)
    const shouldRecalculateRanking = (now - this.lastRankingUpdate) >= this.RANKING_UPDATE_INTERVAL;

    if (shouldRecalculateRanking) {
      this.lastRankingUpdate = now;

      // Step 0a: Recalculate dynamic threshold based on current demand
      this.dynamicThreshold = this.calculateDynamicThreshold();

      // NOTE: Metrics (tps, lastMinuteCount) are updated incrementally in getOrCreateCircleForAddress()
      // No periodic recalculation needed - each new tx just increments the count

      // Step 0b: Remove inactive circles (no transactions for CIRCLE_INACTIVITY_TIMEOUT)
      // IMPORTANT: Never remove saved circles - they persist until manually deleted
      const inactiveKeys: string[] = [];
      for (const [circleKey, circle] of this.circleKeyToCircle) {
        // Skip saved circles - they should never auto-remove
        if (circle.isSaved) continue;

        const timeSinceLastActivity = now - circle.lastActivityTime;
        if (timeSinceLastActivity > this.CIRCLE_INACTIVITY_TIMEOUT) {
          inactiveKeys.push(circleKey);
        }
      }

      // Remove all inactive circles
      if (inactiveKeys.length > 0) {
        for (const circleKey of inactiveKeys) {
          this.removeCircleByKey(circleKey);
        }
      }

      // Step 0c: Remove inactive pending addresses (same timeout as circles)
      const inactivePending: string[] = [];
      for (const [address, pending] of this.pendingAddresses) {
        const timeSinceActivity = now - pending.lastActivityTime;
        if (timeSinceActivity > this.CIRCLE_INACTIVITY_TIMEOUT) {
          inactivePending.push(address);
        }
      }

      // Remove all inactive pending addresses
      if (inactivePending.length > 0) {
        for (const address of inactivePending) {
          this.pendingAddresses.delete(address);
        }
      }

      // Step 1-4: Proportional sizing calculation (based on chartMode)
      // Each circle's size reflects its percentage share of total TPS or GPS
      // Frontend calculates locally for immediate mode switching
      // Backend provides initial historical data (60s window) on page load

      // Step 1: Calculate total rate across all circles
      let totalRate = 0;
      for (const [, circle] of this.circleKeyToCircle) {
        const rate = this.chartMode === 'gas'
          ? circle.gasUsed / 60  // GPS: gas per second
          : circle.tps;    // TPS: transactions per second (from backend)
        totalRate += rate;
      }

      // Step 2: Calculate proportional size for each circle based on its share
      if (totalRate > 0) {
        for (const [, circle] of this.circleKeyToCircle) {
          // Calculate this circle's rate
          const rate = this.chartMode === 'gas'
            ? circle.gasUsed / 60  // GPS: gas per second
            : circle.tps;    // TPS: transactions per second

          // Calculate percentage share of total rate
          const shareRatio = rate / totalRate; // 0.0 to 1.0

          // Logarithmic scaling: compress low values, expand high values
          // This makes high-activity circles stand out more while keeping small circles readable
          // Higher LOG_SCALE = more aggressive compression of small values
          const LOG_SCALE = 9;
          const logRatio = Math.log(1 + shareRatio * LOG_SCALE) / Math.log(1 + LOG_SCALE);

          const targetRadius = this.BASE_RADIUS +
            (this.MAX_RADIUS - this.BASE_RADIUS) * logRatio;

          circle.targetRadius = targetRadius;
        }
      } else {
        // Fallback: if no activity, set all to base radius
        for (const [, circle] of this.circleKeyToCircle) {
          circle.targetRadius = this.BASE_RADIUS;
        }
      }

      // OPTIMIZATION: Only clamp radii periodically (every 200ms) to prevent overlaps
      // This is expensive so we don't do it every frame
      if (now - this.lastRadiusClampUpdate > this.RADIUS_CLAMP_INTERVAL) {
        this.lastRadiusClampUpdate = now;
        for (const [, circle] of this.circleKeyToCircle) {
          const maxSafeRadius = this.getMaxSafeRadius(circle);
          if (circle.targetRadius > maxSafeRadius) {
            circle.targetRadius = maxSafeRadius;
          }
        }
      }
    }

    // Collect circles that need images loaded
    const circlesNeedingImages: Array<{ circle: CircleData; circleKey: string }> = [];

    // Animate ALL circles toward their target radius (happens every frame for smoothness)
    for (const [circleKey, circle] of this.circleKeyToCircle) {
      // Track circles without images that are large enough to show an image
      if (!circle.hasImage && circle.targetRadius >= 10) {
        circlesNeedingImages.push({ circle, circleKey });
      }
      // Also track largest for backwards compatibility
      if (!circle.hasImage && (!largestCircle || circle.targetRadius > largestCircle.circle.targetRadius)) {
        largestCircle = { circle, circleKey };
      }

      // Animate radius towards clamped target (smooth animation every frame)
      // OPTIMIZATION: Only clamp radius every few frames, not every frame
      const radiusDiff = circle.targetRadius - circle.radius;

      // OPTIMIZATION: Increased threshold from 0.5 to 5.0 - reduces GPU pipeline flushes significantly
      if (Math.abs(radiusDiff) > 5.0) {
        // OPTIMIZATION: Delta-time aware interpolation - consistent speed at any FPS
        const radiusLerpFactor = 1 - Math.pow(1 - 0.3, deltaTime / 16.67);
        circle.radius += radiusDiff * radiusLerpFactor;

        // Redraw circle with new radius (use saved color if applicable)
        if (circle.graphics) {
          const circleColor = circle.isSaved ? SAVED_CIRCLE_COLOR : BLOCK_COLOR;
          circle.graphics.clear();
          circle.graphics.circle(0, 0, circle.radius);
          circle.graphics.fill({ color: circleColor, alpha: 0.01 }); // Transparent fill for clickability
          circle.graphics.stroke({ width: 1, color: circleColor });
          // OPTIMIZATION: Use Circle shape directly for hitArea instead of creating new Graphics
          circle.graphics.hitArea = { contains: (x: number, y: number) => x * x + y * y <= circle.radius * circle.radius };
        }

        // Update sprite size to match new circle radius
        if (circle.sprite) {
          const diameter = circle.radius * 2;
          circle.sprite.width = diameter;
          circle.sprite.height = diameter;

          // Update mask to match new radius
          if (circle.sprite.mask && circle.sprite.mask instanceof Graphics) {
            (circle.sprite.mask as Graphics).clear();
            (circle.sprite.mask as Graphics).circle(circle.x, circle.y, circle.radius);
            (circle.sprite.mask as Graphics).fill({ color: 0xFFFFFF });
          }
        }
      }
    }

    // Load images for circles that need them (limit to a few per frame to avoid lag)
    const MAX_IMAGES_PER_FRAME = 3;
    let imagesLoadedThisFrame = 0;

    for (const { circle, circleKey } of circlesNeedingImages) {
      if (imagesLoadedThisFrame >= MAX_IMAGES_PER_FRAME) break;

      // For project keys, look up by project name; for raw addresses, look up via OLI labels
      let project: ReturnType<typeof getProjectByAddress> = undefined;
      if (isProjectKey(circleKey)) {
        const projectName = getProjectNameFromKey(circleKey);
        if (projectName) {
          project = getProjectByName(projectName);
        }
      } else {
        project = getProjectByAddress(circle.address);
      }

      if (project && project.img && !project.img.includes('mega-eth.png')) {
        this.loadImageForCircle(circle, circleKey, project);
        imagesLoadedThisFrame++;
      }
    }

    // Update highlight effects for circles with open modals
    this.updateHighlights(deltaTime);
  }

  /**
   * Update highlight ring effects for circles with open modals
   */
  private updateHighlights(deltaTime: number): void {
    // Update animation phase for pulsing effect
    this.highlightPhase += deltaTime * 0.005; // Slow pulse
    if (this.highlightPhase > Math.PI * 2) {
      this.highlightPhase -= Math.PI * 2;
    }

    // Calculate pulse alpha (oscillates between 0.4 and 0.9)
    const pulseAlpha = 0.65 + 0.25 * Math.sin(this.highlightPhase);

    for (const circleKey of this.highlightedCircleKeys) {
      const circle = this.circleKeyToCircle.get(circleKey);
      if (!circle) continue;

      // Create highlight graphics if needed
      if (!circle.highlightGraphics) {
        circle.highlightGraphics = new Graphics();
        this.container.addChild(circle.highlightGraphics);
      }

      // Draw pulsing highlight ring
      const highlight = circle.highlightGraphics;
      highlight.clear();

      // Outer glow ring
      const glowRadius = circle.radius + 8;
      highlight.circle(circle.x, circle.y, glowRadius);
      highlight.stroke({ width: 4, color: this.HIGHLIGHT_COLOR, alpha: pulseAlpha * 0.5 });

      // Inner bright ring
      const innerRadius = circle.radius + 4;
      highlight.circle(circle.x, circle.y, innerRadius);
      highlight.stroke({ width: 2, color: this.HIGHLIGHT_COLOR, alpha: pulseAlpha });
    }
  }

  /**
   * Remove a circle by its key and cleanup its resources
   */
  private removeCircleByKey(circleKey: string): void {
    const circle = this.circleKeyToCircle.get(circleKey);
    if (!circle) return;

    // Remove from spatial grid
    this.removeFromGrid(circle);

    // Destroy graphics
    if (circle.graphics) {
      this.container.removeChild(circle.graphics);
      circle.graphics.destroy();
    }

    // Destroy sprite and mask
    if (circle.sprite) {
      if (circle.sprite.mask) {
        const mask = circle.sprite.mask as Graphics;
        this.container.removeChild(mask);
        mask.destroy();
      }
      this.container.removeChild(circle.sprite);
      circle.sprite.destroy();
    }

    // Destroy highlight graphics
    if (circle.highlightGraphics) {
      this.container.removeChild(circle.highlightGraphics);
      circle.highlightGraphics.destroy();
    }

    // Remove from highlighted set
    this.highlightedCircleKeys.delete(circleKey);

    // Remove from maps
    this.circleKeyToCircle.delete(circleKey);
    // Also remove reverse lookups for all addresses
    for (const addr of circle.addresses) {
      this.addressToCircleKey.delete(addr);
    }
  }

  /**
   * Load image for a circle from a known dapp project
   * @param circle - The circle to load the image for
   * @param circleKey - The circle key for validation
   * @param project - The project with image URL
   */
  private loadImageForCircle(circle: CircleData, circleKey: string, project: FluffleProject): void {
    if (circle.hasImage) return; // Already loading or loaded

    circle.hasImage = true;
    circle.imageUrl = project.img || null;
    circle.projectData = project;

    if (!project.img) return;

    // For external URLs (Twitter, etc.), use our backend image proxy
    // Local paths (/projects/*) work directly
    let imgUrl = project.img;
    if (project.img.startsWith('http')) {
      // Use backend proxy to avoid CORS issues with external images
      const backendUrl = import.meta.env.VITE_WS_URL?.replace('ws://', 'http://').replace('wss://', 'https://') || '';
      imgUrl = `${backendUrl}/img?url=${encodeURIComponent(project.img)}`;
    }

    // Load image using HTML Image element for better CORS handling
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      if (!this.circleKeyToCircle.has(circleKey)) {
        console.warn(`[CircleRenderer] Circle removed before image loaded: ${project.name}`);
        return;
      }

      try {
        const texture = Texture.from(img);
        const sprite = new Sprite(texture);

        // Size sprite to fill entire circle (use actual current radius, not maxRadius)
        const diameter = circle.radius * 2; // Full diameter
        sprite.width = diameter;
        sprite.height = diameter;

        // Center sprite
        sprite.anchor.set(0.5);
        sprite.x = circle.x;
        sprite.y = circle.y;

        // Make circular with mask (full radius)
        const mask = new Graphics();
        mask.circle(circle.x, circle.y, circle.radius);
        mask.fill({ color: 0xFFFFFF });
        this.container.addChild(mask);
        sprite.mask = mask;

        // Make sprite interactive - use same drag-aware pattern as circle graphics
        sprite.eventMode = 'static';
        sprite.cursor = 'grab';

        // Setup drag handlers on sprite (same as circle graphics)
        this.setupDragHandlers(sprite as any, circle, circle.address);

        this.container.addChild(sprite);
        circle.sprite = sprite;

        console.log(`[CircleRenderer] ✓ Image added for ${project.name} at (${circle.x.toFixed(0)}, ${circle.y.toFixed(0)}) radius=${circle.radius.toFixed(0)}`);
      } catch (error) {
        console.error(`[CircleRenderer] Texture creation failed for ${project.name}:`, error);
        circle.hasImage = false;
      }
    };

    img.onerror = () => {
      // console.error(`[CircleRenderer] Image load failed for ${project.name}`);
      circle.hasImage = false; // Allow retry
      circle.projectData = null;
    };

    img.src = imgUrl;
  }

  /**
   * Check if a potential circle position overlaps with existing circles
   * Uses spatial hashing to only check nearby circles - O(1) instead of O(n)
   * Uses squared distances to avoid expensive Math.sqrt()
   * Includes MIN_CIRCLE_GAP to ensure circles never touch
   */
  private checkOverlap(x: number, y: number, radius: number): boolean {
    // Get grid cell coordinates
    const gridX = Math.floor(x / this.GRID_CELL_SIZE);
    const gridY = Math.floor(y / this.GRID_CELL_SIZE);

    // Check all 9 adjacent cells (including current cell)
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${gridX + dx},${gridY + dy}`;
        const cell = this.spatialGrid.get(key);

        if (!cell) continue;

        // Check each circle in this grid cell
        for (const circle of cell) {
          const distX = x - circle.x;
          const distY = y - circle.y;
          const distSq = distX * distX + distY * distY;

          // Include gap: distance must be >= radius1 + radius2 + gap
          const radiusSum = radius + circle.radius + this.MIN_CIRCLE_GAP;
          const radiusSumSq = radiusSum * radiusSum;

          if (distSq < radiusSumSq) {
            return true; // Overlap or too close
          }
        }
      }
    }

    return false; // No overlap
  }

  /**
   * Check if position is in exclusion zone
   * - Not in top triangle (y < SEQUENCER_CENTER_Y && in top angular zone)
   * - Not within SEQUENCER_EXCLUSION_RADIUS of sequencer center
   * - Not above BLOCKS_BOTTOM
   * Uses squared distance to avoid Math.sqrt()
   */
  private isInExclusionZone(x: number, y: number): boolean {
    // Not above BLOCKS_BOTTOM
    if (y < BLOCKS_BOTTOM) return true;

    // Not within SEQUENCER_EXCLUSION_RADIUS of sequencer (use squared distance)
    const dx = x - SEQUENCER_CENTER_X;
    const dy = y - SEQUENCER_CENTER_Y;
    const distSq = dx * dx + dy * dy;
    const radiusSq = SEQUENCER_EXCLUSION_RADIUS * SEQUENCER_EXCLUSION_RADIUS;
    if (distSq < radiusSq) return true;

    return false;
  }

  /**
   * Get all active circles (for backwards compatibility with TransactionRenderer)
   * Returns array of circle positions
   */
  public getActiveCircles(): Array<{ x: number; y: number; radius: number }> {
    const circles: Array<{ x: number; y: number; radius: number }> = [];
    for (const circle of this.circleKeyToCircle.values()) {
      circles.push({ x: circle.x, y: circle.y, radius: circle.radius });
    }
    return circles;
  }

  /**
   * Convert screen coordinates to world coordinates
   */
  private screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    if (!this.canvasRect) {
      return { x: screenX, y: screenY };
    }
    // Get position relative to canvas, then divide by scale to get world coords
    const canvasX = screenX - this.canvasRect.left;
    const canvasY = screenY - this.canvasRect.top;
    return {
      x: canvasX / this.viewportScale,
      y: canvasY / this.viewportScale
    };
  }

  /**
   * Setup drag event handlers for a circle
   */
  private setupDragHandlers(graphics: Graphics, circle: CircleData, address: string): void {
    let isDragging = false;
    let hasMoved = false;
    let startScreenX = 0;
    let startScreenY = 0;
    const DRAG_THRESHOLD = 5; // Pixels moved before considered a drag

    graphics.on('pointerdown', (event) => {
      // Update canvas rect on each drag start (in case of resize/scroll)
      const canvas = this.container.parent?.parent as any;
      if (canvas?._canvas) {
        this.canvasRect = canvas._canvas.getBoundingClientRect();
      }

      // Start potential drag
      isDragging = true;
      hasMoved = false;
      this.draggedCircle = circle;

      // Store start position in screen coords for threshold check
      startScreenX = event.clientX;
      startScreenY = event.clientY;

      // Calculate offset from circle center to mouse in world coords
      // event.global is already in world coordinates from PixiJS
      const worldPos = this.screenToWorld(event.clientX, event.clientY);
      this.dragOffsetX = circle.x - worldPos.x;
      this.dragOffsetY = circle.y - worldPos.y;

      // Setup document-level move and up handlers
      const onMove = (e: PointerEvent) => {
        if (!isDragging || !this.draggedCircle) return;

        // Check if we've moved enough to consider this a drag (in screen pixels)
        const dx = e.clientX - startScreenX;
        const dy = e.clientY - startScreenY;
        if (!hasMoved && (dx * dx + dy * dy) > DRAG_THRESHOLD * DRAG_THRESHOLD) {
          hasMoved = true;
          this.isDragMode = true;
          graphics.cursor = 'grabbing';
          graphics.alpha = 0.8;

          // Bring to front - graphics, sprite, and mask
          this.container.removeChild(graphics);
          this.container.addChild(graphics);

          // Also bring sprite and mask to front if present
          if (circle.sprite) {
            if (circle.sprite.mask) {
              this.container.removeChild(circle.sprite.mask as Graphics);
              this.container.addChild(circle.sprite.mask as Graphics);
            }
            this.container.removeChild(circle.sprite);
            this.container.addChild(circle.sprite);
            circle.sprite.alpha = 0.8;
          }
        }

        if (!hasMoved) return;

        // Convert screen coords to world coords
        const worldPos = this.screenToWorld(e.clientX, e.clientY);

        // Calculate new position using world coordinates
        let newX = worldPos.x + this.dragOffsetX;
        let newY = worldPos.y + this.dragOffsetY;

        // Clamp to bounds
        newX = Math.max(circle.radius, Math.min(this.SCENE_WIDTH - circle.radius, newX));
        newY = Math.max(this.MIN_Y, Math.min(this.MAX_Y - circle.radius, newY));

        // Update spatial grid
        this.removeFromGrid(circle);
        circle.x = newX;
        circle.y = newY;
        this.addToGrid(circle);

        // Update graphics position
        if (circle.graphics) {
          circle.graphics.x = newX;
          circle.graphics.y = newY;
        }

        // Update sprite position and mask if present
        if (circle.sprite) {
          circle.sprite.x = newX;
          circle.sprite.y = newY;

          // Update mask position (mask uses absolute coords, not relative)
          if (circle.sprite.mask && circle.sprite.mask instanceof Graphics) {
            const mask = circle.sprite.mask as Graphics;
            mask.clear();
            mask.circle(newX, newY, circle.radius);
            mask.fill({ color: 0xFFFFFF });
          }
        }

        // Push other circles away
        this.applyDragRepulsion(circle);
      };

      const onUp = (e: PointerEvent) => {
        isDragging = false;
        this.draggedCircle = null;
        graphics.cursor = 'grab';
        graphics.alpha = 1;

        // Reset sprite alpha if present
        if (circle.sprite) {
          circle.sprite.alpha = 1;
        }

        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);

        // If we moved (it was a drag), save the position
        if (hasMoved) {
          userSettings.saveCirclePosition(address, circle.x, circle.y);
          circle.isSaved = true;

          // Redraw circle with saved color (orange)
          if (circle.graphics) {
            circle.graphics.clear();
            circle.graphics.circle(0, 0, circle.radius);
            circle.graphics.fill({ color: SAVED_CIRCLE_COLOR, alpha: 0.01 });
            circle.graphics.stroke({ width: 1, color: SAVED_CIRCLE_COLOR });
            // OPTIMIZATION: Use contains function instead of new Graphics()
            const r = circle.radius;
            circle.graphics.hitArea = { contains: (x: number, y: number) => x * x + y * y <= r * r };
          }
        }

        // If we didn't move (it was a click), trigger the click callback
        if (!hasMoved && this.onCircleClick) {
          const metrics: CircleMetrics = {
            tps: circle.tps,
            gps: circle.gasUsed / 60,
            lastMinuteCount: circle.lastMinuteCount,
            totalTxCount: circle.txCount,
            totalGasUsed: circle.gasUsed
          };
          // Use getProjectForAddress to look up project from ADDRESS_TO_PROJECT mapping
          // (circle.projectData may be null if image hasn't loaded yet)
          this.onCircleClick(this.getProjectForAddress(address), address, e.clientX, e.clientY, metrics);
        }
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    });
  }

  /**
   * Apply repulsion force to circles near the dragged circle
   * Uses hard separation to prevent any overlap
   */
  private applyDragRepulsion(draggedCircle: CircleData): void {
    // Get nearby circles from spatial grid
    const nearbyCircles = this.getNearbyCircles(draggedCircle.x, draggedCircle.y, draggedCircle.radius + this.MAX_RADIUS * 2);

    for (const other of nearbyCircles) {
      if (other === draggedCircle) continue;

      const dx = other.x - draggedCircle.x;
      const dy = other.y - draggedCircle.y;
      const distSq = dx * dx + dy * dy;
      const minDist = draggedCircle.radius + other.radius + this.MIN_CIRCLE_GAP;
      const minDistSq = minDist * minDist;

      if (distSq < minDistSq && distSq > 0) {
        // Circles are overlapping - IMMEDIATELY separate them
        const dist = Math.sqrt(distSq);
        const overlap = minDist - dist;

        // Normalize direction
        const nx = dx / dist;
        const ny = dy / dist;

        // Hard separation - move the other circle completely out of overlap
        this.removeFromGrid(other);
        other.x += nx * (overlap + 1); // +1 for a small buffer
        other.y += ny * (overlap + 1);

        // Clamp to bounds
        other.x = Math.max(other.radius, Math.min(this.SCENE_WIDTH - other.radius, other.x));
        other.y = Math.max(this.MIN_Y, Math.min(this.MAX_Y - other.radius, other.y));

        this.addToGrid(other);

        // Update graphics position immediately
        if (other.graphics) {
          other.graphics.x = other.x;
          other.graphics.y = other.y;
        }
        if (other.sprite) {
          other.sprite.x = other.x;
          other.sprite.y = other.y;
          // Update mask position (mask uses absolute coordinates)
          if (other.sprite.mask && other.sprite.mask instanceof Graphics) {
            const mask = other.sprite.mask as Graphics;
            mask.clear();
            mask.circle(other.x, other.y, other.radius);
            mask.fill({ color: 0xFFFFFF });
          }
        }

        // If this is a saved (orange) circle, save its new position
        if (other.isSaved) {
          userSettings.saveCirclePosition(other.address, other.x, other.y);
        }

        // Also apply some velocity for smoother cascading movement
        other.velocityX += nx * overlap * this.DRAG_REPULSION_FORCE;
        other.velocityY += ny * overlap * this.DRAG_REPULSION_FORCE;
      }
    }

    // Resolve any secondary overlaps caused by pushing circles into each other
    this.resolveAllOverlaps(draggedCircle);
  }

  /**
   * Resolve all circle-to-circle overlaps (excluding the dragged circle)
   * Uses iterative separation to ensure no overlaps remain
   */
  private resolveAllOverlaps(excludeCircle: CircleData | null): void {
    // OPTIMIZATION: Reduced from 5 to 2 - rarely need more iterations
    const MAX_ITERATIONS = 2;

    for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
      let hasOverlap = false;

      for (const circle of this.circleKeyToCircle.values()) {
        if (circle === excludeCircle) continue;

        // Check against nearby circles
        const nearbyCircles = this.getNearbyCircles(circle.x, circle.y, circle.radius + this.MAX_RADIUS + this.MIN_CIRCLE_GAP);

        for (const other of nearbyCircles) {
          if (other === circle || other === excludeCircle) continue;

          const dx = other.x - circle.x;
          const dy = other.y - circle.y;
          const distSq = dx * dx + dy * dy;
          const minDist = circle.radius + other.radius + this.MIN_CIRCLE_GAP;
          const minDistSq = minDist * minDist;

          if (distSq < minDistSq && distSq > 0) {
            hasOverlap = true;
            const dist = Math.sqrt(distSq);
            const overlap = minDist - dist;

            // Normalize direction
            const nx = dx / dist;
            const ny = dy / dist;

            // Split the separation between both circles (each moves half)
            const separation = (overlap + 1) / 2;

            this.removeFromGrid(circle);
            this.removeFromGrid(other);

            circle.x -= nx * separation;
            circle.y -= ny * separation;
            other.x += nx * separation;
            other.y += ny * separation;

            // Clamp to bounds
            circle.x = Math.max(circle.radius, Math.min(this.SCENE_WIDTH - circle.radius, circle.x));
            circle.y = Math.max(this.MIN_Y, Math.min(this.MAX_Y - circle.radius, circle.y));
            other.x = Math.max(other.radius, Math.min(this.SCENE_WIDTH - other.radius, other.x));
            other.y = Math.max(this.MIN_Y, Math.min(this.MAX_Y - other.radius, other.y));

            this.addToGrid(circle);
            this.addToGrid(other);

            // Update graphics
            if (circle.graphics) {
              circle.graphics.x = circle.x;
              circle.graphics.y = circle.y;
            }
            if (circle.sprite) {
              circle.sprite.x = circle.x;
              circle.sprite.y = circle.y;
              // Update mask position (mask uses absolute coordinates)
              if (circle.sprite.mask && circle.sprite.mask instanceof Graphics) {
                const mask = circle.sprite.mask as Graphics;
                mask.clear();
                mask.circle(circle.x, circle.y, circle.radius);
                mask.fill({ color: 0xFFFFFF });
              }
            }
            if (other.graphics) {
              other.graphics.x = other.x;
              other.graphics.y = other.y;
            }
            if (other.sprite) {
              other.sprite.x = other.x;
              other.sprite.y = other.y;
              // Update mask position (mask uses absolute coordinates)
              if (other.sprite.mask && other.sprite.mask instanceof Graphics) {
                const mask = other.sprite.mask as Graphics;
                mask.clear();
                mask.circle(other.x, other.y, other.radius);
                mask.fill({ color: 0xFFFFFF });
              }
            }

            // Save new positions for any saved (orange) circles that were moved
            if (circle.isSaved) {
              userSettings.saveCirclePosition(circle.address, circle.x, circle.y);
            }
            if (other.isSaved) {
              userSettings.saveCirclePosition(other.address, other.x, other.y);
            }
          }
        }
      }

      // If no overlaps found, we're done
      if (!hasOverlap) break;
    }
  }

  /**
   * Get circles near a position using spatial grid
   */
  private getNearbyCircles(x: number, y: number, radius: number): CircleData[] {
    const nearby: CircleData[] = [];
    const cellRadius = Math.ceil(radius / this.GRID_CELL_SIZE);
    const centerCellX = Math.floor(x / this.GRID_CELL_SIZE);
    const centerCellY = Math.floor(y / this.GRID_CELL_SIZE);

    for (let cx = centerCellX - cellRadius; cx <= centerCellX + cellRadius; cx++) {
      for (let cy = centerCellY - cellRadius; cy <= centerCellY + cellRadius; cy++) {
        const key = `${cx},${cy}`;
        const cellCircles = this.spatialGrid.get(key);
        if (cellCircles) {
          nearby.push(...cellCircles);
        }
      }
    }

    return nearby;
  }

  /**
   * Update physics simulation for drag mode
   * Called every frame to apply velocities and friction
   */
  public updateDragPhysics(): void {
    if (!this.isDragMode) return;

    let hasMovement = false;

    for (const circle of this.circleKeyToCircle.values()) {
      // Skip the currently dragged circle
      if (circle === this.draggedCircle) continue;

      // Apply velocity
      if (Math.abs(circle.velocityX) > this.DRAG_MIN_VELOCITY ||
          Math.abs(circle.velocityY) > this.DRAG_MIN_VELOCITY) {
        hasMovement = true;

        // Update spatial grid before moving
        this.removeFromGrid(circle);

        // Apply velocity
        circle.x += circle.velocityX;
        circle.y += circle.velocityY;

        // Clamp to bounds
        circle.x = Math.max(circle.radius, Math.min(this.SCENE_WIDTH - circle.radius, circle.x));
        circle.y = Math.max(this.MIN_Y, Math.min(this.MAX_Y - circle.radius, circle.y));

        // Update spatial grid after moving
        this.addToGrid(circle);

        // Update graphics position
        if (circle.graphics) {
          circle.graphics.x = circle.x;
          circle.graphics.y = circle.y;
        }

        // Update sprite position if present
        if (circle.sprite) {
          circle.sprite.x = circle.x;
          circle.sprite.y = circle.y;
          // Update mask position (mask uses absolute coordinates)
          if (circle.sprite.mask && circle.sprite.mask instanceof Graphics) {
            const mask = circle.sprite.mask as Graphics;
            mask.clear();
            mask.circle(circle.x, circle.y, circle.radius);
            mask.fill({ color: 0xFFFFFF });
          }
        }

        // Save new position for saved (orange) circles that moved due to velocity
        if (circle.isSaved) {
          userSettings.saveCirclePosition(circle.address, circle.x, circle.y);
        }

        // Apply friction
        circle.velocityX *= this.DRAG_FRICTION;
        circle.velocityY *= this.DRAG_FRICTION;

        // Stop if velocity is very small
        if (Math.abs(circle.velocityX) < this.DRAG_MIN_VELOCITY) circle.velocityX = 0;
        if (Math.abs(circle.velocityY) < this.DRAG_MIN_VELOCITY) circle.velocityY = 0;
      }
    }

    // Resolve any overlaps that occurred from velocity movement
    if (hasMovement) {
      this.resolveAllOverlaps(this.draggedCircle);
    }

    // Exit drag mode if no circle is being dragged and all movement has stopped
    if (!this.draggedCircle && !hasMovement) {
      this.isDragMode = false;
    }
  }

  /**
   * Cleanup when destroying the renderer
   */
  public destroy(): void {
    // Destroy all circles
    for (const circle of this.circleKeyToCircle.values()) {
      if (circle.graphics) {
        this.container.removeChild(circle.graphics);
        circle.graphics.destroy();
      }
    }

    // Clear maps
    this.circleKeyToCircle.clear();
    this.addressToCircleKey.clear();
    this.spatialGrid.clear();

    // console.log('🔵 CircleRenderer destroyed');
  }
}
